// Reusable JSON-LD builders for SEO + AEO (AI answer engines).
import { SITE_URL, SITE_NAME } from "@/Seo";

export const PHONE = "+91-88843-40007";
export const TELEPHONE_TEL = "tel:8884340007";

// Static homepage FAQs — kept in sync with the FAQ section in App.js.
export const HOMEPAGE_FAQS = [
  {
    question: "What will the repair cost me?",
    answer:
      "We'll give you a no-obligation estimate over a quick call or message once we understand the symptom. If a visit is needed, a small inspection charge applies based on distance. After we open up the set, you'll get a firm, transparent figure before any work goes ahead.",
  },
  {
    question: "Is the work guaranteed?",
    answer:
      "Every repair is backed by a six-month promise covering both the parts we fit and the labour we put in. If something we touched plays up again within that window, we'll put it right.",
  },
  {
    question: "How long until my TV is fixed?",
    answer:
      "We aim to sort most sets the very same day, and the vast majority are repaired on the first visit. When a rare part has to be sourced, it may take a day or two longer — and we'll keep you posted the whole way through.",
  },
  {
    question: "Do I have to lug my TV anywhere?",
    answer:
      "Not at all. Our engineers come to your door with the kit and common spares needed to carry out the repair in your home or office, so the heavy set never has to move.",
  },
];

/** Site-wide LocalBusiness — appears on every page. */
export function localBusinessLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}#business`,
    name: SITE_NAME,
    image: `${SITE_URL}/og-image.jpg`,
    url: SITE_URL,
    telephone: PHONE,
    priceRange: "₹₹",
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "Hanumanthappa Compound, 72/1B, JP Nagar 6th Phase, Jarganahalli",
      addressLocality: "Bengaluru",
      addressRegion: "KA",
      postalCode: "560078",
      addressCountry: "IN",
    },
    areaServed: {
      "@type": "City",
      name: "Bangalore",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "10:00",
        closes: "21:00",
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "12",
      bestRating: "5",
      worstRating: "1",
    },
    sameAs: [
      "https://wa.me/918884340007",
      "https://www.google.com/maps/place/TV+CARE/data=!4m2!3m1!1s0x3bae15822c2ddf91:0x632a7367b8bad218",
    ],
  };
}

/** Homepage FAQPage schema — powers Google FAQ rich results + AI answer citations. */
export function faqPageLd(faqs = HOMEPAGE_FAQS) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

/**
 * Brand-specific FAQ template — 4 questions per brand landing page.
 * These deliberately mirror common user searches ("How much does Sony TV repair cost?",
 * "Do you repair all LG models?") so Google's FAQ carousel and Bard/ChatGPT cite them.
 */
export function brandFaqs(brand) {
  return [
    {
      question: `Do you repair all ${brand} TV models in Bangalore?`,
      answer: `Yes — we service every ${brand} television sold in India, from older CRT and Plasma sets right through to the latest LED, LCD, QLED, OLED, Smart and 4K UHD panels. If you can tell us the model number over the phone we'll usually know the common faults before we even arrive.`,
    },
    {
      question: `How much does a ${brand} TV repair cost in Bangalore?`,
      answer: `A minor issue on a ${brand} set — a loose HDMI port, remote pairing or a software glitch — typically starts from around ₹500 including our doorstep visit. Backlight, panel and mainboard repairs are quoted transparently before any work starts, so you'll always know the price up front.`,
    },
    {
      question: `Are the ${brand} spare parts you use genuine?`,
      answer: `Wherever possible we source genuine ${brand} parts through our trusted vendor network. For older or discontinued models we fit high-quality tested compatibles that restore the set to full working order — you'll be told which is being used and why before we install anything.`,
    },
    {
      question: `How quickly can you fix my ${brand} TV?`,
      answer: `Most ${brand} TV faults are diagnosed and repaired on the very first doorstep visit, usually the same day you book. Where a specific spare needs ordering we'll tell you honestly on the call and keep you posted the whole way through.`,
    },
  ];
}

/** Per-service or per-brand landing page — Service schema. */
export function serviceLd({ name, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name: `${name} in Bangalore`,
    description,
    url: `${SITE_URL}${path}`,
    provider: { "@id": `${SITE_URL}#business` },
    areaServed: { "@type": "City", name: "Bangalore" },
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
    },
  };
}

/** Breadcrumb schema for landing pages. */
export function breadcrumbLd(name, path) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: `${SITE_URL}${path}`,
      },
    ],
  };
}

/**
 * Reviews from live data → schema.org Review array attached to the LocalBusiness.
 * Only include a few (top rated) to avoid schema bloat.
 */
export function reviewsLd(reviews = []) {
  if (!reviews.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}#business`,
    review: reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
      },
      author: { "@type": "Person", name: r.client_name },
      reviewBody: r.comment,
    })),
  };
}
